import {
  AuthService,
  ConfigStateService,
  NAVIGATE_TO_MANAGE_PROFILE,
  SessionStateService,
  RoutesService,
  PermissionService,
  eLayoutType,
} from '@abp/ng.core';
import {
  Component,
  OnInit,
  TrackByFunction,
  ViewEncapsulation,
  inject,
  HostListener,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Observable } from 'rxjs';
import { map, filter, startWith } from 'rxjs/operators';
import { eThemeBasicComponents } from '../../models/components';
import { UserMenu, UserMenuService } from '@abp/ng.theme.shared';
@Component({
  standalone: false,
  selector: 'abp-layout-application',
  templateUrl: './application-layout.component.html',
  styleUrls: ['./application-layout.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ApplicationLayoutComponent implements OnInit {
  private readonly sessionState = inject(SessionStateService);
  private readonly authService = inject(AuthService);
  private readonly navigateToManageProfile = inject(NAVIGATE_TO_MANAGE_PROFILE);
  private readonly routesService = inject(RoutesService);
  private readonly userMenuService = inject(UserMenuService);
  private readonly configState = inject(ConfigStateService);
  private readonly permissionService = inject(PermissionService);
  private readonly router = inject(Router);

  isCollapsed = true;
  isSidebarCollapsed = false;
  isMobileMenuOpen = false;
  isUserMenuOpen = false;
  openGroups = new Set<string>();

  get currentUser$(): Observable<any> {
    return this.configState.getOne$('currentUser');
  }

  get currentUserImage$(): Observable<string | null> {
    return this.currentUser$.pipe(
      map((user): string | null => {
        if (user?.isAuthenticated) {
          // Handle avatar from user properties or other sources
          return null;
        }
        return null;
      })
    );
  }

  get smallScreen(): boolean {
    return window.innerWidth < 992;
  }

  get currentUser() {
    return this.configState.getOne('currentUser');
  }

  get appInfo(): { name: string; logoUrl: string | null } {
    const config = this.configState.getAll();
    const defaultResourceName = config?.localization?.defaultResourceName;
    return {
      name: defaultResourceName || 'MyApp',
      logoUrl: null as string | null
    };
  }

  visibleRoutes$: Observable<any[]>;
  navItems$: Observable<any[]>;
  userMenuItems$: Observable<UserMenu[]>;
  pageTitle$: Observable<string>;

  trackByFn: TrackByFunction<any> = (_, element) => element.name || element.id;

  constructor() {
    this.navItems$ = this.routesService.visible$;
    this.userMenuItems$ = this.userMenuService.items$;

    // Create visibleRoutes$ preserving tree structure (children)
    this.visibleRoutes$ = this.navItems$.pipe(
      map(items => {
        return items.filter(item =>
          item.layout === eLayoutType.application &&
          (item.path || (item.name && !item.invisible))
        );
      })
    );

    // Create dynamic page title based on current route
    this.pageTitle$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      startWith(null), // Trigger immediately on init
      map(() => {
        const currentPath = this.router.url.split('?')[0]; // Remove query params
        const routes = this.routesService.flat;

        // Find the route that matches the current path
        const matchedRoute = routes.find(route => {
          if (!route.path) return false;
          // Exact match or starts with the route path
          return currentPath === route.path ||
                 (currentPath.startsWith(route.path) && route.path !== '/');
        });

        return matchedRoute?.name || 'Dashboard';
      })
    );
  }

  ngOnInit() {
    // Component initialization
  }

  toggleGroup(name: string) {
    if (this.openGroups.has(name)) {
      this.openGroups.delete(name);
    } else {
      this.openGroups.add(name);
    }
  }

  isGroupOpen(name: string): boolean {
    return this.openGroups.has(name);
  }

  onLogout() {
    this.authService.logout().subscribe();
  }

  navigateToLogin() {
    this.router.navigate(['/account/login']);
  }

  navigateToManageProfilePage() {
    this.navigateToManageProfile();
  }

  toggleSidebar() {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
  }

  toggleUserMenu() {
    this.isUserMenuOpen = !this.isUserMenuOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const userMenu = target.closest('.user-menu');

    if (!userMenu && this.isUserMenuOpen) {
      this.isUserMenuOpen = false;
    }
  }

  isDropdownChildActive(children: any[]): boolean {
    return children.some(child => window.location.pathname.startsWith(child.path));
  }

  openChange(event: boolean, childrenContainer: HTMLDivElement) {
    if (!event) {
      childrenContainer
        .querySelector('.navigation-backdrop')
        ?.removeEventListener('click', this.clearCollapsed);
    }
  }

  clearCollapsed = () => {
    this.isCollapsed = true;
  };

}
